import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import { leafOrder } from './paneTreeModel';
import { PaneIcon } from './TerminalPaneHeader';
import LayoutThumbnail from './LayoutThumbnail';
import { saveTabAsLayout } from './layoutActions';

/**
 * "Save current layout…": name, remember working directories, resume agent
 * conversations when opened; a live thumbnail and the slot list show exactly
 * what will be stored (never scrollback, env values or tokens).
 */
export default function SaveLayoutModal({ tabId, onClose }) {
  const { t } = useTranslation();
  const tab = useTerminalExtStore((s) => s.tabs.find((x) => x.id === tabId));
  const panes = useTerminalExtStore((s) => s.panes);
  const [name, setName] = useState(tab?.label || '');
  const [rememberCwd, setRememberCwd] = useState(true);
  const [resume, setResume] = useState(true);
  const slots = useMemo(() => (tab ? leafOrder(tab.layout).map((id) => panes[id]).filter(Boolean) : []), [tab, panes]);
  const colors = useMemo(() => Object.fromEntries(slots.filter((p) => p.icon).map((p) => [p.id, p.icon.color])), [slots]);
  if (!tab) return null;
  const hasAgents = slots.some((p) => p.profileId);

  const save = () => {
    if (!name.trim()) return;
    saveTabAsLayout(tabId, { name, rememberCwd, resume });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title={t('terminalExt.saveLayoutTitle', 'Save layout')} subtitle={t('terminalExt.saveLayoutSub', 'Reopen this arrangement of panes in one click')}>
      <form
        className="term-profile-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label className="term-field">
          <span className="modal-logo-label">{t('terminalExt.layoutName', 'Name')}</span>
          <input className="modal-input w-full" value={name} onChange={(e) => setName(e.target.value)} onFocus={(e) => e.target.select()} autoFocus placeholder={t('terminalExt.layoutNamePh', 'e.g. Agents 2×2')} />
        </label>
        <div className="term-save-preview">
          <LayoutThumbnail tree={tab.layout} colors={colors} width={120} height={78} />
          <ul className="term-save-slots">
            {slots.map((p) => (
              <li key={p.id}>
                <PaneIcon pane={p} />
                <span className="term-save-slot-name">{p.label || p.title}</span>
                {rememberCwd && (p.liveCwd || p.cwd) && <span className="term-save-slot-cwd">{p.liveCwd || p.cwd}</span>}
              </li>
            ))}
          </ul>
        </div>
        <label className="term-check">
          <input type="checkbox" className="checkbox-brand" checked={rememberCwd} onChange={(e) => setRememberCwd(e.target.checked)} />
          <span>{t('terminalExt.rememberCwd', 'Remember working directories')}</span>
        </label>
        <label className={`term-check${hasAgents ? '' : ' term-check--muted'}`}>
          <input type="checkbox" className="checkbox-brand" checked={resume} onChange={(e) => setResume(e.target.checked)} />
          <span>
            {t('terminalExt.resumeAgents', 'Resume agent conversations when opened')}
            <span className="term-check-sub">{t('terminalExt.resumeAgentsSub', 'e.g. claude --continue — once per agent and folder')}</span>
          </span>
        </label>
        <div className="modal-footer-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="submit" disabled={!name.trim()} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm">
            {t('common.save', 'Save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
